import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useContext,
  useEffect,
} from 'react';
import {
  Tab,
  Tabs,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';

import Box from '../single_component/Box';
import Button from '../single_component/Button';
import Text from '../single_component/Text';
import TextInput from '../common_component/TextInput';
import UploadInput from '../../components/single_component/UploadInput';
import DocumentBox from '../common_component/DocumentBox';
import ProjectContext from '../../context/ProjectContext';
import SpvContext from '../../context/SpvContext';
import theme from '../../utils/Theme';
import Props from '../../types/props';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import UnsavedChangesModal from '../common_component/UnsavedChangesModal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface NewDocument {
  id: number;
  documentName: string;
  fileName: string;
  file: File | null;
  fileKey: string;
}

interface Folder {
  id: number;
  folderName: string;
  documents: NewDocument[];
}

type Category =
  | 'constitutional'
  | 'kyc'
  | 'financial'
  | 'other'
  | 'promoter';

interface FilesState {
  constitutional: Folder[];
  kyc: Folder[];
  financial: Folder[];
  other: Folder[];
  promoter: Folder[];
}

type DeleteConfirm =
  | { type: 'folder'; folderName: string }
  | { type: 'existingDoc'; docId: number; projectId?: number }
  | {
      type: 'newDoc';
      folderName: string;
      docId: number;
      fileKey: string;
    };

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES: Category[] = [
  'constitutional',
  'kyc',
  'financial',
  'other',
  'promoter',
];

const TAB_LABELS = [
  'Constitutional Documents',
  'KYC Documents',
  'Financial Documents',
  'Other Documents',
  'Promoter Documents',
];

const EXISTING_CATS = new Set<string>([
  'constitutional',
  'kyc',
  'financial',
  'promoter',
]);

const INITIAL_FILES: FilesState = {
  constitutional: [],
  kyc: [],
  financial: [],
  other: [],
  promoter: [],
};

// ─── Component ────────────────────────────────────────────────────────────────

function SpvTabDocuments({ ...props }: Props) {
  const { deleteMoreDocument, getProject } = useContext(ProjectContext);
  const { spvData, removeSpvFolder, getSpv } = useContext(SpvContext);

  // ── State ──────────────────────────────────────────────────────────────────

  const [selectedTab, setSelectedTab] = useState(0);
  const [files, setFiles] = useState<FilesState>(INITIAL_FILES);
  const [docFiles, setDocFiles] = useState<Record<string, File>>({});
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [folderSearch, setFolderSearch] = useState('');
  const [createFolderName, setCreateFolderName] = useState('');
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] =
    useState<DeleteConfirm | null>(null);
  const [isFolderDirty, setIsFolderDirty] = useState(false);
  const [deletedDocIds, setDeletedDocIds] = useState<Set<number>>(new Set());
  // key: `${category}::${folderName}` → existing docs to delete on save
  const [pendingFolderDeletions, setPendingFolderDeletions] = useState<
    Record<string, any[]>
  >({});

  const docIdRef = useRef(0);
  const folderIdRef = useRef(0);

  // ── Navigation warning ─────────────────────────────────────────────────────

  const { showModal, confirmNavigation, cancelNavigation, allowNavigation } =
    useUnsavedChangesWarning(isFolderDirty);

  // ── Derived ────────────────────────────────────────────────────────────────

  const projectData = props?.extra?.document ?? {};
  const existingDocs: any[] = projectData?.documents ?? [];
  const activeCategory: Category = CATEGORIES[selectedTab];

  const existingByCategory = useMemo(() => {
    const result: Record<string, Record<string, any[]>> = {
      constitutional: {},
      kyc: {},
      financial: {},
      other: {},
      promoter: {},
    };
    existingDocs.forEach((doc: any) => {
      const cat: Category = EXISTING_CATS.has(doc.category)
        ? (doc.category as Category)
        : 'other';
      const folderName: string = doc.folderName ?? 'General';
      if (!result[cat][folderName]) result[cat][folderName] = [];
      result[cat][folderName].push(doc);
    });
    return result;
  }, [existingDocs]);

  const allFolderNames = useMemo(() => {
    const existingNames = Object.keys(
      existingByCategory[activeCategory] ?? {}
    ).filter(
      name => !(`${activeCategory}::${name}` in pendingFolderDeletions)
    );
    const newNames = files[activeCategory].map(f => f.folderName);
    return Array.from(new Set(existingNames.concat(newNames)));
  }, [activeCategory, existingByCategory, files, pendingFolderDeletions]);

  const filteredFolderNames = useMemo(() => {
    const q = folderSearch.trim().toLowerCase();
    if (!q) return allFolderNames;
    return allFolderNames.filter(n => n.toLowerCase().includes(q));
  }, [allFolderNames, folderSearch]);

  const selectedFolderData = useMemo(
    () =>
      selectedFolder
        ? (files[activeCategory].find(
            f => f.folderName === selectedFolder
          ) ?? null)
        : null,
    [selectedFolder, activeCategory, files]
  );

  const existingDocsInFolder = useMemo(
    () =>
      selectedFolder &&
      !(`${activeCategory}::${selectedFolder}` in pendingFolderDeletions)
        ? (existingByCategory[activeCategory][selectedFolder] ?? []).filter(
            (doc: any) => !deletedDocIds.has(doc.documentId)
          )
        : [],
    [selectedFolder, activeCategory, existingByCategory, pendingFolderDeletions, deletedDocIds]
  );

  // Notify parent when dirty state changes
  useEffect(() => {
    props?.extra?.onFolderDirtyChange?.(isFolderDirty);
  }, [isFolderDirty]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleTabChange = useCallback((_event: any, v: number) => {
    setSelectedTab(v);
    setSelectedFolder(null);
    setFolderSearch('');
  }, []);

  const handleCreateFolder = useCallback(() => {
    const name = createFolderName.trim();
    if (!name) {
      toast.error('Folder name cannot be empty');
      return;
    }
    if (allFolderNames.includes(name)) {
      toast.error('A folder with this name already exists');
      return;
    }
    const newFolder: Folder = {
      id: ++folderIdRef.current,
      folderName: name,
      documents: [],
    };
    setFiles(prev => ({
      ...prev,
      [activeCategory]: [...prev[activeCategory], newFolder],
    }));
    setSelectedFolder(name);
    setCreateFolderName('');
    setCreateFolderOpen(false);
    setIsFolderDirty(true);
  }, [createFolderName, allFolderNames, activeCategory]);

  const handleDeleteFolder = useCallback(
    async (folderName: string) => {
      const existingInFolder =
        existingByCategory[activeCategory][folderName] ?? [];

      // For existing folders call the dedicated API immediately
      if (existingInFolder.length > 0 && spvData?.spvId) {
        try {
          const res = await removeSpvFolder(
            spvData.spvId,
            activeCategory,
            folderName
          );
          if (!res?.success) {
            toast.error(res?.message ?? 'Failed to delete folder');
            setDeleteConfirm(null);
            return;
          }
          props?.extra?.onFolderDeleted?.(activeCategory, folderName);
          if (props?.extra?.developerId) {
            getSpv(props.extra.developerId);
          }
        } catch {
          toast.error('Failed to delete folder');
          setDeleteConfirm(null);
          return;
        }
      }

      // Track deleted folder so it's excluded from the submit payload
      if (existingInFolder.length > 0) {
        setPendingFolderDeletions(prev => ({
          ...prev,
          [`${activeCategory}::${folderName}`]: existingInFolder,
        }));
      }

      // Clean up any new doc files in local state
      const folder = files[activeCategory].find(
        f => f.folderName === folderName
      );
      if (folder) {
        setDocFiles(prev => {
          const updated = { ...prev };
          folder.documents.forEach(d => {
            delete updated[d.fileKey];
          });
          return updated;
        });
      }

      setFiles(prev => ({
        ...prev,
        [activeCategory]: prev[activeCategory].filter(
          f => f.folderName !== folderName
        ),
      }));
      if (selectedFolder === folderName) setSelectedFolder(null);
      setDeleteConfirm(null);
      toast.success('Folder deleted');
    },
    [
      activeCategory,
      files,
      selectedFolder,
      existingByCategory,
      spvData,
      removeSpvFolder,
      getSpv,
    ]
  );

  const handleAddDocument = useCallback(
    (folderName: string) => {
      const newDocId = ++docIdRef.current;
      const newFolderId = ++folderIdRef.current;
      const newDoc: NewDocument = {
        id: newDocId,
        documentName: '',
        fileName: '',
        file: null,
        fileKey: `${activeCategory}-files-${newDocId}`,
      };
      setFiles(prev => {
        const catFolders = prev[activeCategory];
        const exists = catFolders.some(
          f => f.folderName === folderName
        );
        if (exists) {
          return {
            ...prev,
            [activeCategory]: catFolders.map(f =>
              f.folderName === folderName
                ? { ...f, documents: [...f.documents, newDoc] }
                : f
            ),
          };
        }
        return {
          ...prev,
          [activeCategory]: [
            ...catFolders,
            { id: newFolderId, folderName, documents: [newDoc] },
          ],
        };
      });
    },
    [activeCategory]
  );

  const handleUpdateDocName = useCallback(
    (folderName: string, docId: number, value: string) => {
      setFiles(prev => ({
        ...prev,
        [activeCategory]: prev[activeCategory].map(f =>
          f.folderName === folderName
            ? {
                ...f,
                documents: f.documents.map(d =>
                  d.id === docId
                    ? { ...d, documentName: value }
                    : d
                ),
              }
            : f
        ),
      }));
    },
    [activeCategory]
  );

  const handleUploadFile = useCallback(
    (
      folderName: string,
      docId: number,
      fileKey: string,
      uploadedFiles: File[]
    ) => {
      if (!uploadedFiles?.length) return;
      const file = uploadedFiles[0];
      setDocFiles(prev => ({ ...prev, [fileKey]: file }));
      setFiles(prev => ({
        ...prev,
        [activeCategory]: prev[activeCategory].map(f =>
          f.folderName === folderName
            ? {
                ...f,
                documents: f.documents.map(d =>
                  d.id === docId
                    ? { ...d, file, fileName: file.name }
                    : d
                ),
              }
            : f
        ),
      }));
    },
    [activeCategory]
  );

  const handleRemoveNewDoc = useCallback(
    (folderName: string, docId: number, fileKey: string) => {
      setFiles(prev => ({
        ...prev,
        [activeCategory]: prev[activeCategory].map(f =>
          f.folderName === folderName
            ? {
                ...f,
                documents: f.documents.filter(d => d.id !== docId),
              }
            : f
        ),
      }));
      setDocFiles(prev => {
        const updated = { ...prev };
        delete updated[fileKey];
        return updated;
      });
      setDeleteConfirm(null);
    },
    [activeCategory]
  );

  const handleDeleteExistingDoc = useCallback(
    async (docId: number, projectId?: number) => {
      if (selectedFolder) {
        const folderDocs =
          existingByCategory[activeCategory][selectedFolder] ?? [];
        if (
          folderDocs.length <= 1 &&
          !files[activeCategory].some(f => f.folderName === selectedFolder)
        ) {
          setFiles(prev => ({
            ...prev,
            [activeCategory]: [
              ...prev[activeCategory],
              {
                id: ++folderIdRef.current,
                folderName: selectedFolder,
                documents: [],
              },
            ],
          }));
        }
      }
      if (props?.extra?.handledocdelete) {
        setDeletedDocIds(prev => {
          const next = new Set(prev);
          next.add(docId);
          return next;
        });
        props.extra.handledocdelete(docId);
        setDeleteConfirm(null);
        return;
      }
      try {
        const response = await deleteMoreDocument(docId);
        if (response?.success) {
          toast.success(response?.message);
          if (projectId) await getProject(projectId);
        } else {
          toast.error(
            response?.message ?? 'Failed to delete document'
          );
        }
      } catch {
        toast.error('Failed to delete document');
      }
      setDeleteConfirm(null);
    },
    [
      props?.extra?.handledocdelete,
      deleteMoreDocument,
      getProject,
      selectedFolder,
      existingByCategory,
      activeCategory,
      files,
    ]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'folder') {
      await handleDeleteFolder(deleteConfirm.folderName);
    } else if (deleteConfirm.type === 'existingDoc') {
      handleDeleteExistingDoc(
        deleteConfirm.docId,
        deleteConfirm.projectId
      );
    } else if (deleteConfirm.type === 'newDoc') {
      handleRemoveNewDoc(
        deleteConfirm.folderName,
        deleteConfirm.docId,
        deleteConfirm.fileKey
      );
    }
  }, [
    deleteConfirm,
    handleDeleteFolder,
    handleDeleteExistingDoc,
    handleRemoveNewDoc,
  ]);

  const handleSubmit = useCallback(async () => {
    // Clear dirty state so navigation blocker doesn't fire
    setIsFolderDirty(false);
    allowNavigation();

    const result: Record<string, any[]> = {};
    CATEGORIES.forEach(cat => {
      const flatDocs = files[cat].flatMap(folder =>
        folder.documents
          .filter(
            d => d.documentName.trim() !== '' && d.file !== null
          )
          .map(d => ({
            documentName: d.documentName,
            fileName: d.fileName,
            file: d.fileKey,
            folderName: folder.folderName,
          }))
      );
      if (flatDocs.length > 0) result[cat] = flatDocs;
    });
    props?.extra?.onNext?.({
      documents: JSON.stringify(result),
      docFiles,
    });
  }, [files, docFiles, pendingFolderDeletions, allowNavigation, existingByCategory, props?.extra?.onNext]);

  // ── Tab Sx ─────────────────────────────────────────────────────────────────

  const tabSx = {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.primary.main,
    textTransform: 'none',
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Box
        style={{
          backgroundColor: 'white',
          boxShadow: '0px 2px 10px 0px #10496030',
          borderRadius: 2,
          marginTop: 5,
        }}
      >
        {/* ── Category Tabs ── */}
        <Box
          style={{
            backgroundColor: '#E5F7FF',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            centered
            sx={{
              paddingInline: 5,
              '& .MuiTabs-flexContainer': {
                justifyContent: 'space-between',
              },
              '& .MuiTabs-indicator': {
                background: '#25A9E0',
                height: 3,
              },
            }}
          >
            {TAB_LABELS.map(label => (
              <Tab key={label} label={label} sx={tabSx} />
            ))}
          </Tabs>
        </Box>

        {/* ── Two-Panel Layout ── */}
        <Grid
          container
          sx={{ minHeight: 420, maxHeight: 520, overflow: 'hidden' }}
        >
          {/* Left Panel — Folders */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              borderRight: '1px solid #E0E0E0',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 520,
            }}
          >
            <Box style={{ padding: '12px 16px 8px' }}>
              {/* Search */}
              <TextField
                placeholder="Search folders..."
                value={folderSearch}
                onChange={e => setFolderSearch(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="small"
                        sx={{ color: '#757575' }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              {/* New Folder Button */}
              <MuiButton
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<CreateNewFolderIcon />}
                onClick={() => setCreateFolderOpen(true)}
                sx={{
                  mb: 1.5,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                New Folder
              </MuiButton>

              <Typography
                variant="caption"
                sx={{ color: '#757575', fontWeight: 600 }}
              >
                FOLDERS ({filteredFolderNames.length})
              </Typography>
            </Box>

            <Divider />

            {/* Folder List */}
            {filteredFolderNames.length === 0 ? (
              <Box
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px',
                }}
              >
                <FolderIcon
                  sx={{ fontSize: 40, mb: 1, color: '#BDBDBD' }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: '#9E9E9E', textAlign: 'center' }}
                >
                  No folders yet
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#BDBDBD',
                    textAlign: 'center',
                    mt: 0.5,
                  }}
                >
                  Create a folder to get started
                </Typography>
              </Box>
            ) : (
              <List
                dense
                disablePadding
                sx={{ flex: 1, overflowY: 'auto' }}
              >
                {filteredFolderNames.map(folderName => {
                  const existingCount = (
                    existingByCategory[activeCategory][folderName] ??
                    []
                  ).length;
                  const newCount =
                    files[activeCategory].find(
                      f => f.folderName === folderName
                    )?.documents.length ?? 0;
                  const total = existingCount + newCount;
                  const isSelected = selectedFolder === folderName;

                  return (
                    <ListItemButton
                      key={folderName}
                      selected={isSelected}
                      onClick={() => setSelectedFolder(folderName)}
                      sx={{
                        borderRadius: 0,
                        pr: 1,
                        '&.Mui-selected': {
                          backgroundColor: '#E5F7FF',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: '#d4f0fb',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {isSelected ? (
                          <FolderOpenIcon
                            sx={{ color: '#25A9E0' }}
                          />
                        ) : (
                          <FolderIcon
                            sx={{
                              color: theme.palette.primary.main,
                            }}
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 600 : 400,
                              fontSize: 13,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {folderName}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{ color: '#9E9E9E' }}
                          >
                            {total}{' '}
                            {total === 1 ? 'document' : 'documents'}
                          </Typography>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          setDeleteConfirm({
                            type: 'folder',
                            folderName,
                          });
                        }}
                        sx={{
                          color: '#d32f2f',
                          opacity: 0.55,
                          '&:hover': { opacity: 1 },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Grid>

          {/* Right Panel — Documents */}
          <Grid
            item
            xs={12}
            md={8}
            sx={{ maxHeight: 520, overflowY: 'auto' }}
          >
            {selectedFolder === null ? (
              /* Empty State — no folder selected */
              <Box
                style={{
                  height: '100%',
                  minHeight: 380,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <InsertDriveFileIcon
                  sx={{ fontSize: 48, mb: 1.5, color: '#BDBDBD' }}
                />
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, color: '#757575' }}
                >
                  No folder selected
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#BDBDBD', mt: 0.5 }}
                >
                  Select a folder from the left panel
                </Typography>
              </Box>
            ) : (
              <Box style={{ padding: '16px 24px' }}>
                {/* Folder Header */}
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 2,
                  }}
                >
                  <FolderOpenIcon
                    sx={{ color: '#25A9E0', fontSize: 22 }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 15 }}
                  >
                    {selectedFolder}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Empty state inside folder */}
                {existingDocsInFolder.length === 0 &&
                  (selectedFolderData?.documents.length ?? 0) ===
                    0 && (
                    <Box
                      style={{
                        textAlign: 'center',
                        padding: '24px 0 8px',
                      }}
                    >
                      <InsertDriveFileIcon
                        sx={{
                          fontSize: 36,
                          color: '#BDBDBD',
                          mb: 1,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: '#9E9E9E' }}
                      >
                        No documents in this folder
                      </Typography>
                    </Box>
                  )}
 <Box style={{
                  overflowY: 'auto',
                  maxHeight: '40vh',
                  paddingRight: 1,
                }}>
                {/* Existing documents from API */}
                {existingDocsInFolder.map((doc: any) => (
                  <Box
                    key={doc.documentId}
                    style={{ marginBottom: 0 }}
                  >
                    <DocumentBox
                      extra={{
                        type: 'editMandate',
                        url: doc.documentFile,
                        textlabel: doc.documentName,
                        chiplabel:
                          doc.fileName ?? doc.documentName,
                        handleDeleteDocuments: () =>
                          setDeleteConfirm({
                            type: 'existingDoc',
                            docId: doc.documentId,
                            projectId: doc.projectId,
                          }),
                      }}
                    />
                  </Box>
                ))}

                {/* New documents being added */}

                {(selectedFolderData?.documents ?? []).map(doc => (
                  <Grid
                    container
                    key={doc.id}
                    spacing={2}
                    sx={{ mb: 1 }}
                    alignItems="center"
                  >
                    <Grid item xs={12} sm={5}>
                      <TextInput
                        label="Document Name"
                        placeholder="Enter Document Name"
                        value={doc.documentName}
                        onChange={(e: any) =>
                          handleUpdateDocName(
                            selectedFolder,
                            doc.id,
                            e.target.value
                          )
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <Text
                        style={{
                          color: 'black',
                          marginBottom: '-6px',
                          marginTop: 0,
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        Upload Document
                      </Text>
                      <UploadInput
                        extra={{
                          docName: doc.file ?? '',
                          onchange: (
                            uploadedFiles: File[]
                          ) =>
                            handleUploadFile(
                              selectedFolder,
                              doc.id,
                              doc.fileKey,
                              uploadedFiles
                            ),
                        }}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={2}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        // pb: 1,
                        mt:2.5
                      }}
                    >
                      <IconButton
                        onClick={() =>
                          setDeleteConfirm({
                            type: 'newDoc',
                            folderName: selectedFolder,
                            docId: doc.id,
                            fileKey: doc.fileKey,
                          })
                        }
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                </Box>

                {/* Add Document Button */}
                <Button
                  extra={{ variant: 'outlined' }}
                  onClick={() => handleAddDocument(selectedFolder)}
                  icons={<AddIcon />}
                  style={{ marginTop: 1 }}
                >
                  <Text style={{ fontSize: 12, fontWeight: 600 }}>
                    Add Document
                  </Text>
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Save Button */}
      <Box style={{ marginTop: 4 }}>
        <Button
          extra={{ variant: 'contained' }}
          onClick={handleSubmit}
          style={{
            width: '43%',
            marginLeft: '2%',
            paddingBlock: 1,
          }}
        >
          Save
        </Button>
      </Box>

      {/* ── Create Folder Dialog ── */}
      <Dialog
        open={createFolderOpen}
        onClose={() => {
          setCreateFolderOpen(false);
          setCreateFolderName('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 16 }}>
          Create New Folder
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Folder Name"
            value={createFolderName}
            onChange={e => setCreateFolderName(e.target.value)}
            fullWidth
            autoFocus
            size="small"
            sx={{ mt: 1 }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleCreateFolder();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <MuiButton
            onClick={() => {
              setCreateFolderOpen(false);
              setCreateFolderName('');
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleCreateFolder}
            variant="contained"
            sx={{ bgcolor: '#25A9E0', '&:hover': { bgcolor: '#1a8fbe' } }}
          >
            Create
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 16 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#424242' }}>
            {(() => {
                if (deleteConfirm?.type !== 'folder') {
                  return 'Are you sure you want to delete this document? This action cannot be undone.';
                }
                const fn = (deleteConfirm as any).folderName as string;
                const existingCount =
                  (existingByCategory[activeCategory][fn] ?? []).length;
                const newCount =
                  files[activeCategory].find(f => f.folderName === fn)
                    ?.documents.length ?? 0;
                const total = existingCount + newCount;
                if (total === 0) {
                  return `Are you sure you want to delete the empty folder "${fn}"?`;
                }
                return `This folder contains ${total} document${
                  total === 1 ? '' : 's'
                }. Deleting it will permanently delete all documents inside. This cannot be undone.`;
              })()}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <MuiButton onClick={() => setDeleteConfirm(null)}>
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* ── Unsaved Folder Changes Warning ── */}
      <UnsavedChangesModal
        open={showModal}
        onStay={cancelNavigation}
        onLeave={confirmNavigation}
      />
    </Box>
  );
}

export default SpvTabDocuments;
